import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { ArrowLeft, Tag, Percent, DollarSign, X } from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [secciones, setSecciones] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [selectedSedes, setSelectedSedes] = useState([]);
  const [product, setProduct] = useState(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    price: 0,
    stock: 0,
    images: [],
    seccion: null,
    offerPrice: "",
  });

  // Discount state
  const [discountActive, setDiscountActive] = useState(false);
  const [discountType, setDiscountType] = useState("fixed"); // "fixed" | "percent"
  const [discountValue, setDiscountValue] = useState("");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await API.get(`/products/${id}`);
        const p = res.data?.data?.product ?? res.data?.data ?? {};
        setProduct(p);

        const op = p.offerPrice ? parseFloat(p.offerPrice) : null;
        setForm({
          title: p.title,
          description: p.description ?? "",
          price: parseFloat(p.price) || 0,
          stock: p.stock ?? 0,
          images: p.images ?? [],
          seccion: p.seccion?._id || p.seccion?.id || null,
          offerPrice: op ?? "",
        });

        if (op && parseFloat(p.price) > 0) {
          setDiscountActive(true);
          setDiscountType("fixed");
          setDiscountValue(String(op));
        }

        setSelectedSedes(p.sedes?.map((s) => s._id || s.id) || []);
      } catch {
        toast.error("Error al cargar producto");
      }
    };

    const fetchSeccionesAndSedes = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const companyId = user.company?.id ?? user.companyId;
        const [secRes, sedeRes] = await Promise.all([
          API.get(`/secciones/company/${companyId}`),
          API.get(`/sedes/company/${companyId}`),
        ]);
        setSecciones(secRes.data?.data?.secciones ?? secRes.data?.secciones ?? []);
        setSedes(sedeRes.data?.data?.sedes ?? sedeRes.data?.sedes ?? []);
      } catch {
        toast.error("Error cargando secciones o sedes");
      }
    };

    fetchProduct();
    fetchSeccionesAndSedes();
  }, [id]);

  // Compute offerPrice from discount inputs
  const computedOfferPrice = () => {
    if (!discountActive || !discountValue) return null;
    const price = parseFloat(form.price) || 0;
    if (discountType === "percent") {
      const pct = parseFloat(discountValue);
      if (!pct || pct <= 0 || pct >= 100) return null;
      return Math.round(price * (1 - pct / 100));
    }
    const fixed = parseFloat(discountValue);
    if (!fixed || fixed <= 0 || fixed >= price) return null;
    return Math.round(fixed);
  };

  const discountPct = () => {
    const op = computedOfferPrice();
    const price = parseFloat(form.price) || 0;
    if (!op || !price) return null;
    return Math.round(((price - op) / price) * 100);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (selectedSedes.length === 0) {
      toast.warning("Debe seleccionar al menos una sede");
      return;
    }

    const finalOfferPrice = discountActive ? computedOfferPrice() : null;

    if (discountActive && !finalOfferPrice) {
      toast.warning("El descuento ingresado no es válido");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("description", form.description);
      formData.append("price", form.price);
      formData.append("stock", form.stock);
      formData.append("seccion", form.seccion || "");
      selectedSedes.forEach((s) => formData.append("sedes", s));
      formData.append("offerPrice", finalOfferPrice !== null ? String(finalOfferPrice) : "");

      const hasNewImages = form.images.length > 0 && form.images[0] instanceof File;
      if (hasNewImages) {
        form.images.forEach((file) => formData.append("images", file));
      }

      await API.put(`/products/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Producto actualizado");
      navigate(-1);
    } catch (err) {
      console.log(err);
      toast.error("Error al actualizar");
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) { toast.warn("Máximo 5 imágenes"); return; }
    setForm({ ...form, images: files });
  };

  if (!product)
    return <p className="text-center mt-10 text-gray-500">Cargando...</p>;

  const op = computedOfferPrice();
  const pct = discountPct();
  const price = parseFloat(form.price) || 0;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 mb-4 text-supernova-600 hover:text-supernova-700"
      >
        <ArrowLeft size={20} />
        Volver
      </button>

      <h2 className="text-3xl font-bold mb-6 text-center">Editar publicación</h2>

      <div className="bg-white p-6 rounded-xl shadow-md">
        <form onSubmit={handleUpdate} className="flex flex-col gap-4">

          {/* Título */}
          <label className="font-semibold">Título</label>
          <input
            className="p-3 border rounded-xl"
            placeholder="Título"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />

          {/* Descripción */}
          <label className="font-semibold">Descripción</label>
          <textarea
            className="p-3 border rounded-xl min-h-[120px]"
            placeholder="Descripción"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          {/* Precio */}
          <label className="font-semibold">Precio</label>
          <input
            className="p-3 border rounded-xl"
            type="number"
            placeholder="Precio"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
            required
          />

          {/* ── Descuento ─────────────────────────────────────────────────────── */}
          <div className="border rounded-xl overflow-hidden">
            {/* Toggle header */}
            <button
              type="button"
              onClick={() => { setDiscountActive(!discountActive); setDiscountValue(""); }}
              className={`w-full flex items-center justify-between px-4 py-3 transition-all
                ${discountActive ? "bg-orange-50 border-b border-orange-100" : "bg-gray-50"}`}
            >
              <div className="flex items-center gap-2">
                <Tag size={16} className={discountActive ? "text-orange-500" : "text-gray-400"} />
                <span className={`font-semibold text-sm ${discountActive ? "text-orange-600" : "text-gray-600"}`}>
                  Aplicar descuento
                </span>
              </div>
              <div className={`w-10 h-5 rounded-full transition-all relative ${discountActive ? "bg-orange-500" : "bg-gray-200"}`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${discountActive ? "left-5" : "left-0.5"}`} />
              </div>
            </button>

            {discountActive && (
              <div className="p-4 space-y-3">
                {/* Tipo de descuento */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setDiscountType("fixed"); setDiscountValue(""); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-sm font-semibold transition-all
                      ${discountType === "fixed"
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"}`}
                  >
                    <DollarSign size={14} /> Precio fijo
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDiscountType("percent"); setDiscountValue(""); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-sm font-semibold transition-all
                      ${discountType === "percent"
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"}`}
                  >
                    <Percent size={14} /> Porcentaje
                  </button>
                </div>

                {/* Input del descuento */}
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max={discountType === "percent" ? 99 : price - 1}
                    step={discountType === "percent" ? 1 : 100}
                    placeholder={discountType === "percent" ? "Ej: 20 (20%)" : "Precio con descuento"}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="w-full p-3 pl-4 pr-12 border rounded-xl focus:ring-2 focus:ring-orange-300 outline-none"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
                    {discountType === "percent" ? "%" : "$"}
                  </span>
                </div>

                {/* Preview del resultado */}
                {op && price > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Precio original</p>
                      <p className="text-sm text-gray-400 line-through">${price.toLocaleString("es-CO")}</p>
                    </div>
                    <div className="text-center">
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        -{pct}% OFF
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Precio con descuento</p>
                      <p className="text-lg font-black text-green-600">${op.toLocaleString("es-CO")}</p>
                    </div>
                  </div>
                )}

                {/* Quitar descuento */}
                <button
                  type="button"
                  onClick={() => { setDiscountActive(false); setDiscountValue(""); }}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X size={12} /> Quitar descuento
                </button>
              </div>
            )}
          </div>

          {/* Stock */}
          <label className="font-semibold">Stock</label>
          <input
            className="p-3 border rounded-xl"
            type="number"
            placeholder="Stock"
            value={form.stock}
            onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })}
          />

          {/* Sección */}
          <label className="font-semibold">Sección</label>
          <select
            className="p-3 border rounded-xl"
            value={form.seccion || ""}
            onChange={(e) => setForm({ ...form, seccion: e.target.value || null })}
          >
            <option value="">Sin sección</option>
            {secciones.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          {/* Sedes */}
          <label className="font-semibold mt-2">Sedes *</label>
          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border p-2 rounded">
            {sedes.map((s) => (
              <label key={s.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  value={s.id}
                  checked={selectedSedes.includes(s.id)}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedSedes([...selectedSedes, s.id]);
                    else setSelectedSedes(selectedSedes.filter((i) => i !== s.id));
                  }}
                  className="accent-supernova-500"
                />
                {s.name}
              </label>
            ))}
          </div>

          {/* Imágenes */}
          <label className="font-semibold mt-2">Imágenes</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageUpload}
            className="p-3 border rounded-xl"
          />
          {form.images.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {form.images.map((img, i) => {
                const url = img instanceof File
                  ? URL.createObjectURL(img)
                  : getImageUrl(img);
                return (
                  <img key={i} src={url} alt="preview" className="w-full h-32 rounded-lg object-cover border" />
                );
              })}
            </div>
          )}

          <motion.button
            whileTap={{ scale: 0.97 }}
            type="submit"
            className="bg-supernova-500 text-white py-3 rounded-xl font-semibold hover:bg-supernova-700 transition mt-2"
          >
            Guardar cambios
          </motion.button>
        </form>
      </div>
    </div>
  );
}
